import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { batches, csv } from '../api/index.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Upload as UploadIcon, FileText, CheckCircle, AlertCircle, Table2 } from 'lucide-react';

type UploadState = 'idle' | 'uploading' | 'success' | 'duplicate' | 'error';

export function Upload() {
  const [state, setState] = useState<UploadState>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<{ batchId: string; isCsv?: boolean } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  async function handleFile(file: File) {
    const name = file.name.toLowerCase();
    const isCsv = name.endsWith('.csv');
    const isXml = name.endsWith('.xml');

    if (!isCsv && !isXml) {
      setErrorMsg('Supported formats: .xml (ONIX) or .csv');
      setState('error');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setErrorMsg('File too large (max 50MB)');
      setState('error');
      return;
    }

    setState('uploading');
    try {
      if (isCsv) {
        const res = await csv.uploadCsv(file);
        if (res.alreadyApplied) {
          setResult({ batchId: res.batchId });
          setState('duplicate');
        } else {
          // Navigate directly to mapper — don't stay on this page
          navigate(`/batches/${res.batchId}/map`);
        }
      } else {
        const res = await batches.upload(file);
        setResult({ batchId: res.batchId });
        setState(res.alreadyApplied ? 'duplicate' : 'success');
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setState('error');
    }
  }

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Upload File</h1>
        <p className="text-gray-500 text-sm mt-1">Upload an ONIX XML file or a CSV spreadsheet to begin the ingest pipeline</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select File</CardTitle>
        </CardHeader>
        <CardContent>
          {(state === 'idle' || state === 'error') && (
            <>
              <label
                htmlFor="file-upload"
                onDrop={onDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={`
                  flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-12 cursor-pointer transition-colors
                  ${dragOver ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-brand-400 hover:bg-gray-50'}
                `}
              >
                <UploadIcon className={`w-12 h-12 mb-3 ${dragOver ? 'text-brand-500' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700">Drop a file here</p>
                <p className="text-xs text-gray-400 mt-1">or click to browse</p>
                <input id="file-upload" type="file" accept=".xml,.csv" className="hidden" onChange={onFileInput} />
              </label>

              {/* Format hints */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-3">
                  <FileText size={18} className="text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-700">ONIX XML</p>
                    <p className="text-xs text-gray-400 mt-0.5">Starts pipeline immediately</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-3">
                  <Table2 size={18} className="text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-700">CSV Spreadsheet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Column mapping step first</p>
                  </div>
                </div>
              </div>

              {state === 'error' && (
                <div className="mt-4 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <AlertCircle size={16} /> {errorMsg}
                </div>
              )}
            </>
          )}

          {state === 'uploading' && (
            <div className="flex flex-col items-center py-12 gap-3">
              <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-brand-500 animate-spin" />
              <p className="text-sm text-gray-500">Uploading…</p>
            </div>
          )}

          {(state === 'success' || state === 'duplicate') && result && (
            <div className="flex flex-col items-center py-8 gap-4">
              <CheckCircle className={`w-12 h-12 ${state === 'duplicate' ? 'text-yellow-500' : 'text-green-500'}`} />
              <div className="text-center">
                <p className="font-semibold text-gray-900">
                  {state === 'duplicate' ? 'Duplicate file — batch already exists' : 'File uploaded successfully!'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {state === 'duplicate'
                    ? 'This exact file was already processed.'
                    : 'The pipeline is running. You\'ll see results once parsing completes.'}
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => navigate(`/batches/${result.batchId}`)}>
                  <FileText size={16} /> View Batch
                </Button>
                <Button variant="secondary" onClick={() => { setState('idle'); setResult(null); }}>
                  Upload Another
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 text-xs text-gray-500 space-y-1">
        <p>• ONIX: supports 2.1 and 3.0 XML — pipeline starts immediately</p>
        <p>• CSV: column mapping required before pipeline starts</p>
        <p>• Maximum file size: 50MB</p>
        <p>• Uploading the same file twice will not create a duplicate batch</p>
      </div>
    </div>
  );
}
